import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Star, Users, Fuel, Gauge, ArrowRight, Flame, CalendarCheck, Sparkles } from "lucide-react";
import { CATEGORY_LABELS, currency } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const tagIcon = (tag) => {
  const t = (tag || "").toLowerCase();
  if (t.includes("demand")) return <Flame className="h-3 w-3" />;
  if (t.includes("week") || t.includes("available")) return <CalendarCheck className="h-3 w-3" />;
  return <Sparkles className="h-3 w-3" />;
};

const tagStyle = (tag) => {
  const t = (tag || "").toLowerCase();
  if (t.includes("demand") || t.includes("limited")) return "badge-warning";
  if (t.includes("week") || t.includes("available")) return "badge-availability";
  return "badge-blue";
};

const isCutoutImage = (url) => {
  const u = (url || "").toLowerCase();
  return u.includes("remove_bg") || u.includes("curbo") || u.includes("lion-transparent") || (u.endsWith(".png") && !u.includes("unsplash"));
};

export const VehicleCard = ({ vehicle, showMemberBadge = true }) => {
  const { user } = useAuth();
  const isMember = !!user?.is_member;
  const cat = CATEGORY_LABELS[vehicle.category] || vehicle.category;
  const isCutout = isCutoutImage(vehicle.image_url);
  const weeklySavings = Math.max(0, (vehicle.price_weekly || 0) - (vehicle.member_price_weekly || 0));
  return (
    <Card className="card-clean card-clean-hover p-0 overflow-hidden group" data-testid="vehicle-card">
      <Link to={`/vehicles/${vehicle.id}`} className="block" data-testid={`vehicle-card-link-${vehicle.id}`}>
        <div className="relative aspect-[16/10] overflow-hidden bg-[hsl(var(--charcoal-100))] flex items-center justify-center">
          <img
            src={vehicle.image_url}
            alt={vehicle.name}
            className={`w-full h-full transition-transform duration-300 group-hover:scale-[1.03] ${isCutout ? "object-contain p-4 drop-shadow-[0_16px_20px_rgba(15,23,42,0.12)]" : "object-cover"}`}
            data-testid="vehicle-card-image"
            loading="lazy"
            onError={(e) => { e.currentTarget.style.objectFit = "contain"; e.currentTarget.style.padding = "1rem"; e.currentTarget.src = "/brand/alfadrive-lion-transparent.png"; }}
          />
          <div className="absolute top-3 left-3 hidden sm:flex flex-wrap gap-1.5 max-w-[80%]">
            {(vehicle.urgency_tags || []).slice(0, 2).map((t) => (
              <span key={t} className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-medium ${tagStyle(t)}`}>
                {tagIcon(t)} {t}
              </span>
            ))}
          </div>
          <div className="absolute top-3 right-3 hidden sm:block rounded-full bg-white/90 text-foreground text-[11px] px-2 py-1 border border-border">
            {cat}
          </div>
          {!vehicle.available && (
            <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
              <span className="text-foreground text-sm font-medium">Currently unavailable</span>
            </div>
          )}
        </div>
      </Link>
      <div className="p-3.5 sm:p-5">
        <div className="flex items-start justify-between gap-2 sm:gap-3">
          <div className="min-w-0">
            <p className="text-[10px] sm:text-[11px] uppercase tracking-widest text-muted-foreground truncate">{vehicle.brand}</p>
            <h3 className="font-display text-sm sm:text-lg font-semibold leading-tight truncate" data-testid="vehicle-card-title">{vehicle.name}</h3>
          </div>
          <div className="inline-flex items-center gap-1 text-[10px] sm:text-xs text-muted-foreground shrink-0">
            <Star className="h-3 w-3 sm:h-3.5 sm:w-3.5 fill-[hsl(var(--gold-stamp))] text-[hsl(var(--gold-stamp))]" />
            {vehicle.rating?.toFixed?.(1) || "4.9"}
          </div>
        </div>

        <div className="mt-2 sm:mt-3 hidden sm:flex flex-wrap gap-1.5 text-[11px] text-muted-foreground">
          <span className="inline-flex items-center gap-1 bg-secondary rounded-full px-2 py-1"><Users className="h-3 w-3" /> {vehicle.seats}</span>
          <span className="inline-flex items-center gap-1 bg-secondary rounded-full px-2 py-1"><Gauge className="h-3 w-3" /> {vehicle.transmission}</span>
          <span className="inline-flex items-center gap-1 bg-secondary rounded-full px-2 py-1"><Fuel className="h-3 w-3" /> {vehicle.fuel_type}</span>
        </div>

        <div className="mt-3 sm:mt-4 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 sm:gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">From</div>
            <div className="flex items-baseline gap-1.5 sm:gap-2 flex-wrap">
              {isMember ? (
                <>
                  <span className="font-display text-lg sm:text-2xl font-semibold tabular-nums text-[hsl(var(--blue-700))]" data-testid="vehicle-card-member-price">{currency(vehicle.member_price_weekly)}</span>
                  <span className="text-[10px] sm:text-xs text-muted-foreground">/wk · member</span>
                </>
              ) : (
                <>
                  <span className="font-display text-lg sm:text-2xl font-semibold tabular-nums" data-testid="vehicle-card-standard-price">{currency(vehicle.price_weekly)}</span>
                  <span className="text-[10px] sm:text-xs text-muted-foreground">/wk</span>
                </>
              )}
            </div>
            {showMemberBadge && !isMember && weeklySavings > 0 && (
              <div className="mt-1 hidden sm:inline-flex items-center gap-1 text-[11px] text-[hsl(var(--blue-700))]">
                <Sparkles className="h-3 w-3" />
                <span className="font-medium tabular-nums" data-testid="vehicle-card-member-savings">Members save {currency(weeklySavings)}/week</span>
              </div>
            )}
            {isMember && weeklySavings > 0 && (
              <div className="mt-1 text-[10px] sm:text-[11px] text-muted-foreground line-through tabular-nums" data-testid="vehicle-card-standard-strikethrough">
                {currency(vehicle.price_weekly)}/wk
              </div>
            )}
          </div>
          <Button asChild size="sm" className="rounded-xl bg-[hsl(var(--primary))] hover:bg-[hsl(var(--blue-500))] w-full sm:w-auto shrink-0 h-9 sm:h-10 px-3 sm:px-4 text-xs sm:text-sm justify-center" data-testid="vehicle-card-reserve-button">
            <Link to={`/vehicles/${vehicle.id}`}>Reserve <ArrowRight className="ml-1 h-3.5 w-3.5 sm:h-4 sm:w-4" /></Link>
          </Button>
        </div>
      </div>
    </Card>
  );
};
